import React from 'react';
import styled from 'styled-components';
import { AspectRatio } from 'react-aspect-ratio';
import PropTypes from 'prop-types';
import marginBottomEnumReturner from '../../../utils/marginBottomEnumReturner';

const SquaredUpThumbnailWrapper = styled.div`
  padding: 0;

  /* CSS Variables */
  /* https://www.npmjs.com/package/react-aspect-ratio */
  [style*='--aspect-ratio'] > :first-child {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
  }

  [style*='--aspect-ratio']::before {
    content: '';
    display: block;
  }
  /* Measures padding if the aspect ratio is NOT 1/1 */
  @supports not (aspect-ratio: 1/1) {
    [style*='--aspect-ratio']::before {
      height: 0;
      padding-bottom: calc(100% / (var(--aspect-ratio)));
    }
  }
  /* Measures Aspect Ratio styling if 1/1 */
  @supports (aspect-ratio: 1/1) {
    [style*='--aspect-ratio']::before {
      aspect-ratio: calc(var(--aspect-ratio));
    }
  }

  /* //IE10/11 only */
  @media screen and(-ms-high-contrast: active), (-ms-high-contrast: none) {
    .ie-polyfill {
      padding-top: 50% !important;
    }
  }
`;

function AspectRatioPlugin({
  src,
  height,
  ratio,
  alt,
  className,
  imageBackground,
  borderRadius,
  backgroundSize,
  backgroundPosition,
  marginBottom // enum value.  Provides ability to override margin-bottom property
}) {
  const imgHeight = height || '';
  const classNames = className || '';
  const backgroundSizes = backgroundSize || 'contain';

  const backgroundPositions = backgroundPosition || 'center';
  // By default (null), has 0 mb
  // marginBottom can be used as values 1-6 (Example: 0=0px, 1=8px, 2=16px and so forth)
  const marginBottomOverride = marginBottomEnumReturner(marginBottom);

  let borderTopRadius = '0px';
  if (borderRadius === 1) {
    borderTopRadius = '0px';
  } else if (typeof borderRadius === 'string') {
    borderTopRadius = borderRadius;
  }

  const styles = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    height: imgHeight || 'auto',
    backgroundImage: `url(${src})`,
    backgroundColor: `${imageBackground}`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: `${backgroundPositions}`,
    backgroundSize: `${backgroundSizes}`,
    borderTopLeftRadius: `${borderTopRadius}`,
    borderTopRightRadius: `${borderTopRadius}`,
    marginBottom: marginBottomOverride
  };

  return (
    <SquaredUpThumbnailWrapper>
      <AspectRatio
        style={styles}
        role="img"
        aria-label={alt || ''}
        ratio={ratio || '1/1'}
        className={`ie-polyfill ${classNames}`}
      />
    </SquaredUpThumbnailWrapper>
  );
}

AspectRatioPlugin.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  ratio: PropTypes.string,
  height: PropTypes.string,
  className: PropTypes.string,
  marginBottom: PropTypes.number,
  imageBackground: PropTypes.string,
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  backgroundSize: PropTypes.string,
  backgroundPosition: PropTypes.string
};

// Use media: { key: value} for this component
AspectRatioPlugin.defaultProps = {
  src: null,
  alt: null,
  ratio: '1/1',
  height: null,
  className: null,
  marginBottom: null,
  imageBackground: 'transparent',
  borderRadius: 0,
  backgroundSize: 'contain',
  backgroundPosition: null
};

export default AspectRatioPlugin;
